import { useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Circle, Eye, Upload, RefreshCw, ScanText, Radio } from "lucide-react";
import { get, post, api, patch, errMsg, inr, dt, label, PRODUCT, BASE } from "@/lib/api";
import { useAuth } from "@/context/Auth";
import { Card, StatusBadge, Btn, Loading, Field, Table, useFetch } from "@/components/kit";

const DOCS = ["PAN", "AADHAAR", "SALARY_SLIP", "BANK_STATEMENT", "ITR", "GST_CERTIFICATE", "BUSINESS_PROOF", "ADDRESS_PROOF", "PROPERTY_DOCUMENT", "VEHICLE_QUOTATION", "PHOTO", "CANCELLED_CHEQUE", "OTHER"];
const TIMELINE = ["LEAD_CREATED", "ELIGIBILITY", "KYC_COMPLETED", "CREDIT_CHECK", "LENDER_SUBMISSION", "OFFER_RECEIVED", "CUSTOMER_ACCEPTED", "SANCTION", "DISBURSEMENT", "DISBURSED", "CLOSED"];
const NEEDED = ["KYC", "CREDIT_BUREAU", "LENDER_SHARING", "AA"];

function Timeline({ app }) {
  const reached = new Set(app.history.map((h) => h.to));
  const at = (s) => app.history.find((h) => h.to === s)?.at;
  return (
    <ol className="space-y-0" data-testid="application-timeline">
      {TIMELINE.map((s, i) => {
        const done = reached.has(s);
        const cur = app.stage === s;
        return (
          <li key={s} className="relative flex gap-3 pb-4">
            {i < TIMELINE.length - 1 && <span className={`absolute left-[9px] top-5 h-full w-0.5 ${done ? "bg-emerald-500" : "bg-slate-200"}`} />}
            {done ? <CheckCircle2 className={`relative z-10 h-5 w-5 shrink-0 ${cur ? "text-blue-900" : "text-emerald-600"}`} /> : <Circle className="relative z-10 h-5 w-5 shrink-0 text-slate-300" />}
            <div>
              <p className={`text-sm ${cur ? "font-bold text-blue-900" : done ? "font-medium text-slate-800" : "text-slate-400"}`}>{app.stage_labels[s]}</p>
              {at(s) && <p className="text-[11px] text-slate-400">{dt(at(s))}</p>}
            </div>
          </li>
        );
      })}
      {app.stage === "REJECTED" && <li className="flex gap-3"><StatusBadge value="REJECTED" /></li>}
    </ol>
  );
}

function Consents({ app, reload }) {
  const have = new Set(app.consents.map((c) => c.type));
  const missing = NEEDED.filter((t) => !have.has(t));
  const give = async (types) => { try { await post(`/applications/${app.id}/consents`, { types }); toast.success("Consent recorded"); reload(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <Card title="Consents" testid="consents-card" action={missing.length > 0 && <Btn data-testid="grant-all-consents-btn" className="h-7 px-3 text-xs" onClick={() => give(missing)}>Grant {missing.length} pending</Btn>}>
      <div className="flex flex-wrap gap-2">
        {NEEDED.concat(["COMMUNICATION", "PRIVACY_POLICY", "TERMS", "OFFER_ACCEPTANCE"]).map((t) => (
          <span key={t} data-testid={`consent-status-${t}`} className={`rounded-full border px-2.5 py-1 text-[11px] font-semibold ${have.has(t) ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-slate-200 text-slate-400"}`}>{have.has(t) ? "✓ " : ""}{label(t)}</span>
        ))}
      </div>
    </Card>
  );
}

function KycForm({ app, run }) {
  const [f, setF] = useState({ name: app.customer_name, gstin: app.data.gstin || "", aadhaar_last4: app.data.aadhaar_last4 || "", bank_ifsc: app.data.bank_ifsc || "" });
  const P = { form: f, setForm: setF };
  return (
    <div className="space-y-3">
      <p className="text-sm text-slate-600">PAN on file: <b className="font-mono">{app.data.pan_masked || "not provided"}</b>. Provide PAN again only to update it.</p>
      <div className="grid gap-3 md:grid-cols-3">
        <Field label="PAN (optional)" name="pan" {...P} />
        <Field label="Name" name="name" {...P} />
        <Field label="Aadhaar last 4" name="aadhaar_last4" {...P} />
        <Field label="Bank account (optional)" name="bank_account_no" {...P} />
        <Field label="IFSC" name="bank_ifsc" {...P} />
        {app.product === "BL" && <Field label="GSTIN" name="gstin" {...P} />}
      </div>
      <Btn data-testid="run-kyc-btn" onClick={() => run(`/applications/${app.id}/kyc`, Object.fromEntries(Object.entries(f).filter(([, v]) => v)))}>Verify KYC (mock provider)</Btn>
    </div>
  );
}

function Match({ app, run, results }) {
  const elig = (results || []).filter((r) => r.eligible);
  const [sel, setSel] = useState(elig.map((r) => r.lender_id));
  return (
    <div className="space-y-3" data-testid="lender-match-panel">
      <p className="text-xs text-slate-500">Matching is indicative and based on configured lender rules. Final credit decision rests with the lender.</p>
      {(results || []).map((r) => (
        <label key={r.lender_id} className={`flex items-start gap-3 rounded-lg border p-3 ${r.eligible ? "border-emerald-200" : "border-slate-200 opacity-70"}`} data-testid={`match-${r.lender_code}`}>
          <input type="checkbox" disabled={!r.eligible} checked={sel.includes(r.lender_id)} onChange={() => setSel(sel.includes(r.lender_id) ? sel.filter((x) => x !== r.lender_id) : [...sel, r.lender_id])} className="mt-1" />
          <div className="flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <p className="font-semibold">{r.lender_name}</p>
              {r.is_demo && <span className="rounded bg-amber-100 px-1.5 text-[10px] font-bold text-amber-800">DEMO</span>}
              <span className="text-[11px] text-slate-400">{r.lender_type} · Priority {r.priority}</span>
              <span className={`text-xs font-bold ${r.eligible ? "text-emerald-700" : "text-rose-600"}`}>{r.eligible ? "Potentially eligible" : "Not eligible"}</span>
            </div>
            {r.eligible ? <p className="text-xs text-slate-600">Up to {inr(r.max_eligible)} · {r.estimated_terms.interest_range} · {r.estimated_terms.tenure} · EMI ~{inr(r.estimated_terms.indicative_emi)}</p>
              : <p className="text-xs text-rose-600">{r.reasons.join("; ")}</p>}
            <p className="text-[11px] text-slate-400">Docs: {r.required_documents.map(label).join(", ")}</p>
          </div>
        </label>
      ))}
      {app.stage === "CREDIT_CHECK" && <Btn data-testid="submit-lenders-btn" disabled={!sel.length} onClick={() => run(`/applications/${app.id}/submit`, { lender_ids: sel }, { headers: { "Idempotency-Key": `submit-${app.id}-${sel.join("-")}` } })}>Submit to {sel.length} lender(s)</Btn>}
    </div>
  );
}

function Offers({ app, reload }) {
  const [consent, setConsent] = useState(false);
  const accept = async (o) => {
    if (!consent) return toast.error("Please tick the acceptance consent");
    try { await post(`/offers/${o.id}/accept`, { consent: true }); toast.success("Offer accepted"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  if (!app.offers.length) return <p className="text-sm text-slate-400">No offers yet.</p>;
  return (
    <div className="space-y-3" data-testid="offers-panel">
      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        {app.offers.map((o) => (
          <div key={o.id} data-testid={`offer-card-${o.lender_name.replace(/\W+/g, "-")}`} className={`rounded-xl border p-4 ${o.status === "ACCEPTED" ? "border-emerald-500 bg-emerald-50/40" : "border-slate-200"}`}>
            <div className="flex items-center justify-between"><p className="font-semibold">{o.lender_name}</p>{o.is_demo && <span className="rounded bg-amber-100 px-1.5 text-[10px] font-bold text-amber-800">DEMO</span>}</div>
            <p className="mt-2 font-display text-2xl font-bold text-blue-900">{inr(o.loan_amount)}</p>
            <div className="mt-2 grid grid-cols-2 gap-1 text-xs text-slate-600">
              <span>Rate: <b>{o.interest_rate}%</b></span><span>Tenure: <b>{o.tenure_months}m</b></span>
              <span>EMI: <b>{inr(o.emi)}</b></span><span>Fee: <b>{inr(o.processing_fee)}</b></span>
              <span className="col-span-2 text-slate-400">Expires {dt(o.expires_at)}</span>
            </div>
            <div className="mt-3 flex items-center justify-between"><StatusBadge value={o.status} />
              {app.stage === "OFFER_RECEIVED" && o.status === "AVAILABLE" && <Btn data-testid={`accept-offer-${o.id}`} variant="success" className="h-8 text-xs" onClick={() => accept(o)}>Accept</Btn>}</div>
          </div>
        ))}
      </div>
      {app.stage === "OFFER_RECEIVED" && (
        <label className="flex items-start gap-2 text-xs text-slate-600"><input data-testid="offer-consent-checkbox" type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5" />
          I accept the selected lender's offer and terms. I understand the lender will complete its own underwriting before sanction.</label>
      )}
    </div>
  );
}

function Docs({ app, reload, staff }) {
  const [type, setType] = useState("PAN");
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("application_id", app.id); fd.append("doc_type", type); fd.append("file", file);
    setBusy(true);
    try { await api.post("/documents/upload", fd); toast.success("Uploaded"); reload(); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const view = async (d) => { try { const r = await get(`/documents/${d.id}/signed-url`); window.open(`${BASE}${r.url}`, "_blank"); } catch (e) { toast.error(errMsg(e)); } };
  const review = async (d, status) => { try { await patch(`/documents/${d.id}/review`, { status, remarks: status === "REJECTED" ? "Please re-upload a clear copy" : "OK" }); reload(); } catch (e) { toast.error(errMsg(e)); } };
  const ocr = async (d) => { try { await post("/kyc/document/verify", { document_id: d.id }); toast.success("Extracted - human review required"); reload(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-2">
        <select data-testid="doc-type-select" className="fo-input max-w-xs" value={type} onChange={(e) => setType(e.target.value)}>{DOCS.map((d) => <option key={d} value={d}>{label(d)}</option>)}</select>
        <label className={`inline-flex h-10 cursor-pointer items-center gap-2 rounded-md bg-blue-900 px-4 text-sm font-semibold text-white ${busy ? "opacity-50" : ""}`}>
          <Upload className="h-4 w-4" />{busy ? "Uploading…" : "Upload (PDF/JPG/PNG ≤10MB)"}
          <input data-testid="doc-file-input" type="file" accept=".pdf,.png,.jpg,.jpeg" className="hidden" onChange={(e) => upload(e.target.files[0])} />
        </label>
      </div>
      <Table testid="documents-table" rows={app.documents} cols={[
        { k: "doc_type", h: "Type", r: (d) => label(d.doc_type) }, { k: "filename", h: "File" }, { k: "version", h: "Ver" },
        { k: "status", h: "Status", r: (d) => <StatusBadge value={d.status} /> },
        { k: "ocr", h: "OCR", r: (d) => d.ocr ? <span className="text-xs">{Math.round(d.ocr.confidence * 100)}% · {label(d.ocr.review_status)}</span> : "—" },
        { k: "a", h: "", r: (d) => (
          <div className="flex gap-1">
            <button data-testid={`doc-view-${d.id}`} onClick={() => view(d)} className="rounded p-1 hover:bg-slate-100" title="Preview (signed URL)"><Eye className="h-4 w-4" /></button>
            {staff && <button onClick={() => ocr(d)} className="rounded p-1 hover:bg-slate-100" title="AI/OCR extract"><ScanText className="h-4 w-4" /></button>}
            {staff && <button data-testid={`doc-verify-${d.id}`} onClick={() => review(d, "VERIFIED")} className="rounded px-2 text-xs font-semibold text-emerald-700 hover:bg-emerald-50">Verify</button>}
            {staff && <button onClick={() => review(d, "REJECTED")} className="rounded px-2 text-xs font-semibold text-rose-600 hover:bg-rose-50">Reject</button>}
          </div>) },
      ]} />
    </div>
  );
}

function Action({ app, run, reload, isStaff, can }) {
  const s = app.stage;
  if (s === "LEAD_CREATED") return <Btn data-testid="check-eligibility-btn" onClick={() => run(`/applications/${app.id}/eligibility`)}>Check eligibility</Btn>;
  if (s === "ELIGIBILITY" || s === "KYC_INITIATED") return <KycForm app={app} run={run} />;
  if (s === "KYC_COMPLETED") return <Btn data-testid="credit-check-btn" onClick={() => run(`/applications/${app.id}/credit-check`)}>Run credit bureau check (mock)</Btn>;
  if (s === "CREDIT_CHECK" || s === "LENDER_SUBMISSION") return <Match app={app} run={run} results={app.match_results} />;
  if (s === "OFFER_RECEIVED") return <Offers app={app} reload={reload} />;
  if (["CUSTOMER_ACCEPTED", "SANCTION", "DISBURSEMENT"].includes(s)) return (
    <div className="space-y-3">
      <p className="text-sm text-slate-600">Application is with <b>{app.selected_lender_name}</b> for processing. The lender performs its own underwriting.</p>
      <div className="flex flex-wrap gap-2">
        <Btn data-testid="sync-lender-btn" onClick={() => run(`/applications/${app.id}/sync-lender`)}><RefreshCw className="h-4 w-4" />Refresh lender status</Btn>
        {can("webhook.manage") && ["SANCTIONED", "DISBURSED"].map((st) => (
          <Btn key={st} variant="outline" data-testid={`simulate-webhook-${st}`} onClick={() => run("/admin/webhooks/simulate", { application_id: app.id, status: st })}><Radio className="h-4 w-4" />Simulate signed webhook: {label(st)}</Btn>
        ))}
      </div>
    </div>
  );
  if (s === "DISBURSED") return (
    <div className="space-y-2 text-sm" data-testid="disbursement-details">
      <p>Disbursed <b>{inr(app.disbursement?.disbursed_amount)}</b> · Net {inr(app.disbursement?.net_disbursed)} · UTR <span className="font-mono">{app.disbursement?.utr}</span></p>
      {isStaff && can("application.edit") && <Btn variant="outline" data-testid="close-application-btn" onClick={() => run(`/applications/${app.id}/transition`, { to: "CLOSED", note: "Completed" })}>Mark completed / close</Btn>}
    </div>
  );
  return <p className="text-sm text-slate-500">Application {label(s)}.</p>;
}

export default function Journey() {
  const { id } = useParams();
  const { user, can } = useAuth();
  const { data: app, reload, loading } = useFetch(() => get(`/applications/${id}`), [id]);
  const [tab, setTab] = useState("documents");
  const isStaff = !["customer", "dsa"].includes(user.role);
  const run = async (url, body, cfg) => {
    try { const r = await post(url, body, cfg); toast.success(r.message); } catch (e) {
      const errs = e?.response?.data?.data?.errors;
      toast.error(errMsg(e) + (errs ? `: ${errs.map((x) => x.error).join("; ")}` : ""));
    }
    reload();
  };
  if (loading && !app) return <Loading />;
  if (!app) return <p>Not found</p>;
  const tabs = [["documents", "Documents"], ["details", "Details"], ["kyc", "KYC & Credit"], ["lenders", "Lender status"], ["history", "Audit history"]];
  return (
    <div className="space-y-6" data-testid="application-journey">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <p className="font-mono text-xs text-slate-500" data-testid="app-code">{app.app_code} · {app.lead_code}</p>
          <h1 className="font-display text-2xl font-bold">{app.customer_name} · {PRODUCT[app.product]}</h1>
          <p className="text-sm text-slate-500">{inr(app.loan_amount)} · {app.tenure_months || "—"} months · {app.city || ""}</p>
        </div>
        <div className="flex items-center gap-2"><span className="text-xs text-slate-500">Stage</span><StatusBadge testid="app-stage" value={app.stage} /><StatusBadge value={app.kyc_status === "VERIFIED" ? "VERIFIED" : "KYC_PENDING"} /></div>
      </div>
      <div className="grid gap-6 lg:grid-cols-12">
        <div className="space-y-6 lg:col-span-8">
          <Card title={`Next step · ${app.stage_labels[app.stage] || app.stage}`} testid="next-step-card"><Action app={app} run={run} reload={reload} isStaff={isStaff} can={can} /></Card>
          <Consents app={app} reload={reload} />
          {app.stage !== "OFFER_RECEIVED" && app.offers.length > 0 && <Card title="Offers"><Offers app={app} reload={reload} /></Card>}
          <div className="rounded-xl border border-slate-200 bg-white">
            <div className="flex gap-1 overflow-x-auto border-b px-3">{tabs.map(([k, l]) => <button key={k} data-testid={`tab-${k}`} onClick={() => setTab(k)} className={`whitespace-nowrap px-3 py-3 text-sm font-semibold ${tab === k ? "border-b-2 border-blue-900 text-blue-900" : "text-slate-500"}`}>{l}</button>)}</div>
            <div className="p-5">
              {tab === "documents" && <Docs app={app} reload={reload} staff={isStaff} />}
              {tab === "details" && <div className="grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2">{Object.entries(app.data).filter(([k]) => !["consents"].includes(k)).map(([k, v]) => <p key={k}><span className="text-slate-400">{label(k)}:</span> <span className="font-medium">{String(v)}</span></p>)}</div>}
              {tab === "kyc" && (
                <div className="space-y-4">
                  {app.credit && <div className="rounded-lg bg-slate-50 p-4 text-sm" data-testid="credit-summary">{app.credit.restricted ? "Credit data restricted for your role." : <>Bureau score <b className="text-lg">{app.credit.score}</b> · DPD(12m) {app.credit.max_dpd_12m} · Enquiries {app.credit.enquiries_6m} · Active {app.credit.active_accounts} · Overdue {inr(app.credit.overdue_amount)} · Write-off {app.credit.write_off ? "Yes" : "No"}</>}</div>}
                  <Table rows={app.kyc_checks} cols={[{ k: "type", h: "Check" }, { k: "provider", h: "Provider" }, { k: "status", h: "Status", r: (c) => <StatusBadge value={c.status} /> }, { k: "r", h: "Reason", r: (c) => c.result.reason || "—" }, { k: "created_at", h: "At", r: (c) => dt(c.created_at) }]} />
                  <Btn variant="outline" data-testid="bank-analysis-btn" onClick={() => run(`/applications/${app.id}/bank-analysis`)}>Run bank statement / AA analysis (mock)</Btn>
                  {app.bank_analysis && <p className="text-xs text-slate-600">Avg credit {inr(app.bank_analysis.avg_monthly_credit)} · Avg EOD {inr(app.bank_analysis.avg_eod_balance)} · EMI {inr(app.bank_analysis.emi_detected)} · Bounces {app.bank_analysis.bounces_6m} · Cash-flow score {app.bank_analysis.cash_flow_score}</p>}
                </div>
              )}
              {tab === "lenders" && <Table rows={app.lender_applications} cols={[{ k: "lender_name", h: "Lender" }, { k: "external_id", h: "Lender App ID", r: (l) => <span className="font-mono text-xs">{l.external_id}</span> }, { k: "status", h: "Status", r: (l) => <StatusBadge value={l.status} /> }, { k: "updated_at", h: "Updated", r: (l) => dt(l.updated_at) }]} />}
              {tab === "history" && <Table rows={[...app.history].reverse()} cols={[{ k: "to", h: "Stage", r: (h) => <StatusBadge value={h.to} /> }, { k: "actor_name", h: "Actor", r: (h) => `${h.actor_name || "—"} (${label(h.actor_role)})` }, { k: "note", h: "Note" }, { k: "at", h: "At", r: (h) => dt(h.at) }]} />}
            </div>
          </div>
        </div>
        <div className="space-y-6 lg:col-span-4">
          <Card title="Application timeline"><Timeline app={app} /></Card>
          {app.sanction && <Card title="Sanction"><p className="text-sm">ID <span className="font-mono">{app.sanction.sanction_id}</span><br />Amount {inr(app.sanction.sanctioned_amount)}<br />{dt(app.sanction.sanctioned_at)}</p></Card>}
          {app.payouts?.length > 0 && <Card title="Payouts">{app.payouts.map((p) => <div key={p.id} className="mb-2 flex justify-between text-sm"><span>{p.channel} · {p.beneficiary_name}</span><span className="font-semibold">{inr(p.net_payable)} <StatusBadge value={p.status} /></span></div>)}</Card>}
          <Card title="Support"><p className="text-xs text-slate-500">Questions about this application? Call +91-9824053365 or email info@finorbitfinance.com. FinOrbit is an LSP; sanction & disbursement are by the lender.</p></Card>
        </div>
      </div>
    </div>
  );
}
